"use client";
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useRouter } from "next/navigation";
import { apiFetch } from "@/utils/api";
import Image from "next/image";
// import { getSafeImageUrl } from "@/utils/images";
import Carousel from "@/components/ui/Carousel";
import Link from "next/link";
import {
  CheckCircleIcon,
  ExternalLinkIcon,
  UsersIcon,
  CopyIcon,
  SearchIcon,
} from "@/utils/icons";
import { Info, ChevronDown } from "lucide-react";

// Interfaces 
interface Offer {
  _id: string;
  store: string;
  storeIcon?: string;
  title: string;
  slug: string;
  description: string;
  terms: string;
  discount: string;
  discountType: 'code' | 'general';
  discountTypeFlat: string;
  discountTypeUnit: string;
  oldValue: string;
  oldValueUnit: string;
  category: string; // Used for filtering
  couponCode: string;
  validTill: string;
  usedBy: number;
  rating: number;
  color: string;
  badge: string;
  bgGradient: string;
  isActive: boolean;
  allowRedirectUrl: boolean;
  redirectUrl: string;
}
interface Store {
  _id?: string;
  name: string;
  slug: string;
  icon: string;
  media_link?: string;
  description?: string;
  brandHeading: string;
  contentDescription?: string;
  website?: string;
  categories?: string[];
  category?: string; // Legacy field
  isActive?: boolean;
  offersCount?: number;
  coupons?: number;
}

interface Category {
  _id: string;
  name: string;
  slug: string;
}

// --- Loading Skeleton Component ---
const LoadingSkeleton = () => (
  <div className="bg-gray-50 pt-0 lg:pb-3 min-h-screen">
    <div className="md:block hidden bg-white relative h-80 md:h-72 md:-mt-24"></div>
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6 lg:p-8 py-4 px-2 pt-0 -mt-0 md:-mt-52 relative z-10 animate-pulse">
      <aside className="w-full md:w-64 space-y-4">
        {/* Store Card Skeleton */}
        <div className="bg-white rounded-xl shadow-lg p-6 h-64 border border-primary-100">
          <div className="w-20 h-20 bg-gray-200 rounded-lg mx-auto mb-4" />
          <div className="h-6 bg-gray-200 rounded w-3/4 mx-auto mb-4" />
          <div className="h-4 bg-gray-100 rounded w-1/3 mx-auto" />
        </div>

        {/* Filter Panel Skeleton */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-primary-100 space-y-4">
          <div className="flex justify-between items-center">
            <div className="h-5 bg-gray-200 rounded w-1/3" />
            <div className="h-4 bg-gray-100 rounded w-1/6" />
          </div>
          <div className="h-10 bg-gray-100 rounded" />
          <div className="h-4 bg-gray-200 rounded w-2/3" />
          <div className="space-y-2">
            <div className="h-4 bg-gray-100 rounded w-full" />
            <div className="h-4 bg-gray-100 rounded w-5/6" />
            <div className="h-4 bg-gray-100 rounded w-3/4" />
            <div className="h-4 bg-gray-100 rounded w-full" />
          </div>
        </div>

        {/* Carousel Skeleton */}
        <div className="bg-white rounded-xl shadow-lg p-6 h-64 border border-primary-100">
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-4" />
          <div className="space-y-3">
            <div className="h-3 bg-gray-100 rounded w-4/5" />
            <div className="h-3 bg-gray-100 rounded w-3/5" />
            <div className="h-3 bg-gray-100 rounded w-4/5" />
          </div>
        </div>
      </aside>
      <main className="flex-1 space-y-6">
        <div className="bg-white rounded-xl shadow-lg p-8 h-40 hidden md:block" />
        <div className="bg-white rounded-xl shadow-xl p-4 lg:p-8 h-48 border border-gray-100" />
        <div className="bg-white rounded-xl shadow-xl p-4 lg:p-8 h-48 border border-gray-100" />
      </main>
    </div>
  </div>
);

export default function StoreDetailPage() {
  const params = useParams();
  const router = useRouter();
  const slug =
    typeof params === "object" && params !== null && "slug" in params
      ? (params as Record<string, string>).slug
      : undefined;
  const [store, setStore] = useState<Store | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastVerified, setLastVerified] = useState<string>("");
  const [allOffers, setAllOffers] = useState<Offer[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [copied, setCopied] = useState<string | null>(null);
  const [openTermsId, setOpenTermsId] = useState<string | null>(null);

  // FILTER STATES
  const [selectedCategorySlug, setSelectedCategorySlug] = useState<string | null>(null);
  const [categorySearchTerm, setCategorySearchTerm] = useState<string>("");
  const [selectedDiscountType, setSelectedDiscountType] = useState<'code' | 'general' | null>(null); // NEW STATE ADDED

  const toggleTerms = (id: string) => {
    setOpenTermsId((current) => (current === id ? null : id));
  };

  function createMarkup() {
    return { __html: store?.contentDescription || "" };
  }

  // --- Data Fetching Effect ---
  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    let mounted = true;

    const fetchStore = apiFetch<{ success: boolean; data: Store }>(`/stores/slug/${slug}`)
      .then((res) => {
        if (mounted) {
          setStore(res.data);
          setError(null);
          setLastVerified(
            new Date().toLocaleDateString("en-IN", {
              weekday: "short",
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })
          );
        }
      })
      .catch((err: unknown) => {
        const message =
          err && typeof err === "object" && "message" in err
            ? (err as { message?: string }).message
            : undefined;
        if (mounted) {
          setError(message || "Failed to load store");
          setStore(null);
        }
      });

    // Fetch Offers
    const fetchOffers = apiFetch<unknown>(`/offers/store/${slug}`)
      .then((res) => {
        let arr: unknown = [];
        if (Array.isArray(res)) {
          arr = res;
        } else if (res && typeof res === "object") {
          const obj = res as Record<string, unknown>;
          if (
            obj.data &&
            typeof obj.data === "object" &&
            Array.isArray((obj.data as Record<string, unknown>).offers)
          ) {
            arr = (obj.data as Record<string, unknown>).offers;
          } else if (Array.isArray(obj.offers)) {
            arr = obj.offers;
          } else if (Array.isArray(obj.data)) {
            arr = obj.data;
          }
        }
        const validOffers = Array.isArray(arr) ? arr : [];
        if (mounted) {
          const enhancedOffers: Offer[] = (validOffers).map(o => ({
            ...(o as Offer),
            discountType: o.couponCode && o.couponCode.length > 0 ? 'code' : 'general',
          }))
          setAllOffers(enhancedOffers); // Store all offers
        }
      })
      .catch((err: unknown) => {
        const message =
          err && typeof err === "object" && "message" in err
            ? (err as { message?: string }).message
            : undefined;
        if (mounted) {
          setError(message || "Failed to load offers");
          setAllOffers([]);
        }
      });

    // Fetch Categories
    const fetchCategories = apiFetch<{ success: boolean; data: Category[] }>(`/categories?isActive=true`)
      .then(res => {
        if (mounted && res.data) {
          const formattedCategories = res.data.map(cat => ({
            ...cat,
            name: cat.name.replaceAll('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
          }));
          setCategories(formattedCategories);
        }
      })

    Promise.allSettled([fetchStore, fetchOffers, fetchCategories])
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [slug]);

  // 1. Filter Categories based on search term (client-side)
  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(categorySearchTerm.toLowerCase())
  );

  // 2. Filter Offers based on the selected filters (Category AND Discount Type)
  const offers = useMemo(() => {
    let filtered = allOffers;

    // --- 1. Apply Category Filter ---
    if (selectedCategorySlug) {
      const normalizedSelectedSlug = selectedCategorySlug.toLowerCase();

      filtered = filtered.filter(offer => {
        const offerCategorySlug = offer.category
          ? offer.category.toLowerCase()
          : '';

        const matches = offerCategorySlug === normalizedSelectedSlug;
        return matches;
      });
    }

    // --- 2. Apply Discount Type Filter---
    if (selectedDiscountType) {
      filtered = filtered.filter(offer =>
        offer.discountType === selectedDiscountType
      );
    }
    return filtered;
  }, [allOffers, selectedCategorySlug, selectedDiscountType]);

  // --- Category Handler ---
  const handleCategorySelection = (slug: string) => {
    // If the same category is clicked, unselect it (toggle off)
    if (selectedCategorySlug === slug) {
      setSelectedCategorySlug(null);
    } else {
      setSelectedCategorySlug(slug);
    }
  };

  // --- Discount Type Handler  ---
  const handleDiscountTypeSelection = (type: 'code' | 'general') => {
    // If the same type is clicked, unselect it (toggle off)
    if (selectedDiscountType === type) {
      setSelectedDiscountType(null);
    } else {
      setSelectedDiscountType(type);
    }
  };

  // --- Clear Filters Handler  ---
  const handleClearFilters = () => {
    setSelectedCategorySlug(null);
    setSelectedDiscountType(null);
    setCategorySearchTerm("");
    console.log("Filter: ALL filters cleared.");
  };

  const handleCopy = async (code: string) => {
    let success = false;
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(code);
        success = true;
      } catch { /* ignored */ }
    }
    if (!success) {
      const textarea = document.createElement("textarea");
      textarea.value = code;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand("copy");
        success = true;
      } catch { /* ignored */ }
      document.body.removeChild(textarea);
    }
    if (success) {
      setCopied(code);
      setTimeout(() => setCopied(null), 1200);
    }
  };

  const handleCouponActionClick = (offer: Offer) => {
    if (offer.couponCode) {
      handleCopy(offer.couponCode);
    }
    const internalOfferUrl = `/offers/${offer.slug}`;
    const couponLoaderUrl = `/coupon-loader?slug=${offer.slug}`;

    window.open(internalOfferUrl, "_blank", "noopener,noreferrer");
    window.location.href = couponLoaderUrl;
  };

  const handleGeneralActionClick = (offer: Offer) => {
    if (offer.couponCode) {
      handleCopy(offer.couponCode);
    }
    const internalOfferUrl = `/offers/${offer.slug}`;
    const couponLoaderUrl = `/coupon-loader?slug=${offer.slug}`;

    window.open(couponLoaderUrl, "_blank", "noopener,noreferrer");

    window.location.href = internalOfferUrl;
  };


  // --- Initial Loading/Error States ---
  if (loading) return <LoadingSkeleton />;
  if (error) return <div className="p-8 text-red-500">Error: {error}</div>;
  if (!store) return <div className="p-8 text-gray-500">Store not found.</div>;

  const NavLinks = [
    { name: "Categories", href: "/category" },
    { name: "Stores", href: "/store" },
    { name: "Offers", href: "/offers" },
  ];

  const categoryName = store.category ? store.category.replaceAll("_", " ") : null;
  const breadcrumbItems = [
    { name: "Home", href: "/" },
    { name: "Stores", href: "/store" },
    ...(categoryName
      ? [{ name: categoryName, href: `/category/${store.category}` }]
      : []),
    { name: store.name, href: `/store/${store.slug}`, current: true },
  ];

  return (
    <div className="bg-gray-50 pt-0 lg:pb-3 min-h-screen">
      <div className="md:block hidden bg-gradient-to-r from-primary-600 to-purple-700 text-white relative h-88 sm:h-72 md:h-72">
      </div>

      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6 lg:p-8 md:px-4 py-4 px-2 pt-0 -mt-0 lg:-mt-72 md:-mt-64 relative z-10">
        <aside className="w-full md:w-64 flex-shrink-0">

          {/* Breadcrumb Navigation  */}
          <nav className="hidden md:block mb-4" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-1 text-xs text-white">
              {breadcrumbItems.map((item, index) => (
                <li key={item.name} className="flex items-center">
                  {item.current ? (
                    <span className="break w-full text-white">{item.name}</span>
                  ) : (
                    <Link
                      href={item.href}
                      className="hover:text-white transition-colors capitalize text-primary-200"
                    >
                      {item.name}
                    </Link>
                  )}
                  {index < breadcrumbItems.length - 1 && (
                    <svg
                      className="flex-shrink-0 w-3 h-3 ml-2 text-primary-300"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"></path>
                    </svg>
                  )}
                </li>
              ))}
            </ol>
          </nav>

          {/* Desktop Store Info Card  */}
          <div className="hidden md:flex flex-col w-full bg-white rounded-xl shadow-lg p-6 mb-4 border border-primary-100 items-center overflow-hidden">
            {/* <Image
              src={store.icon ||
                store.media_link ||
                "/assets/OfferCodeFlight.jpg"}
              alt={store.name}
              width={100}
              height={100}
              className="object-contain w-24 h-24 rounded-xl shadow-md"
            /> */}
            <Image
                    src={store.icon ||
                      store.media_link ||
                      "/assets/OfferCodeFlight.jpg"}
                    alt={store.name}
                    width={56}
                    height={56}
                    className="object-contain bg-white w-24 h-24 rounded-xl border shadow-sm"
                  />
            <div className="text-xl font-bold text-center mb-1 mt-3 text-gray-800">{store.name}</div>
            <div className="text-xs mb-4 flex items-center gap-1 text-green-600">
              <CheckCircleIcon className="w-4 h-4" /> Verified On: {lastVerified}
            </div>

            <div className="w-full h-0.5 bg-gray-200 my-3 rounded-full" />

            <div className="flex justify-between w-full text-center">
              <div className="flex-1">
                <div className="text-2xl font-extrabold text-primary-600 leading-none">{Number(store.coupons)}</div>
                <p className="text-xs text-gray-500 font-medium mt-1">Coupons</p>
              </div>
              <div className="w-px bg-gray-200"></div>
              <div className="flex-1">
                <div className="text-2xl font-extrabold text-primary-600 leading-none">{Number(store.offersCount) - Number(store.coupons) || 0}</div>
                <p className="text-xs text-gray-500 font-medium mt-1">Offers</p>
              </div>
            </div>
          </div>

          {/* Category & Discount Type Filter Panel */}
          <div className="hidden md:block w-full bg-white rounded-xl shadow-lg p-4 mb-4 border border-primary-100">
            <div className="flex justify-between items-center pb-2 border-b border-gray-100 mb-4">
              <div className="text-base font-bold text-gray-800">Filter</div>
              <button
                onClick={handleClearFilters}
                className="text-xs text-primary-600 hover:text-primary-700 font-semibold"
              >
                Clear All
              </button>
            </div>

            {/* Search Input */}
            <div className="relative mb-6">
              <input
                type="text"
                placeholder="Search in Filters"
                value={categorySearchTerm}
                onChange={(e) => setCategorySearchTerm(e.target.value)}
                className="w-full py-2 pl-3 pr-10 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
              />
              <SearchIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>

            {/* Categories Filter List */}
            <div className="pb-3 border-b border-gray-100 mb-3">
              <div className="flex justify-between items-center mb-2 cursor-pointer">
                <h3 className="text-sm font-semibold text-gray-800">Categories</h3>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </div>

              {/* Scrollable Checkbox List */}
              <div className="max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((cat) => (
                    <div
                      key={cat.slug}
                      onClick={() => handleCategorySelection(cat.slug)}
                      className={`flex items-center py-2 text-sm text-gray-700 rounded-md transition-colors cursor-pointer ${selectedCategorySlug === cat.slug ? 'bg-primary-100 font-semibold text-primary-700' : 'hover:bg-gray-50'
                        }`}
                    >
                      {/* Custom Checkbox */}
                      <div className={`w-4 h-4 border rounded mr-2 flex-shrink-0 flex items-center justify-center ${selectedCategorySlug === cat.slug ? 'bg-primary-600 border-primary-600' : 'bg-white border-gray-300'
                        }`}>
                        {selectedCategorySlug === cat.slug && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                        )}
                      </div>
                      <span className="truncate">{cat.name}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-gray-500 py-2">No categories found.</div>
                )}
              </div>
            </div>

            {/* Discount Type Filter List */}
            <div className="pt-3 border-t border-gray-100">
              <div className="flex justify-between items-center mb-2 cursor-pointer">
                <h3 className="text-sm font-semibold text-gray-800">Discount Type</h3>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </div>

              {/* List of Discount Types */}
              <div className="space-y-2">
                {/* Coupon Code Filter */}
                <div
                  onClick={() => handleDiscountTypeSelection('code')}
                  className={`flex items-center py-2 text-sm text-gray-700 rounded-md transition-colors cursor-pointer ${selectedDiscountType === 'code' ? 'bg-primary-100 font-semibold text-primary-700' : 'hover:bg-gray-50'
                    }`}
                >
                  {/* Custom Checkbox */}
                  <div className={`w-4 h-4 border rounded mr-2 flex-shrink-0 flex items-center justify-center ${selectedDiscountType === 'code' ? 'bg-primary-600 border-primary-600' : 'bg-white border-gray-300'
                    }`}>
                    {selectedDiscountType === 'code' && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                    )}
                  </div>
                  <span className="truncate">Coupon Codes ({allOffers.filter(o => o.discountType === 'code').length})</span>
                </div>

                {/* General Offer Filter */}
                <div
                  onClick={() => handleDiscountTypeSelection('general')}
                  className={`flex items-center py-2 text-sm text-gray-700 rounded-md transition-colors cursor-pointer ${selectedDiscountType === 'general' ? 'bg-primary-100 font-semibold text-primary-700' : 'hover:bg-gray-50'
                    }`}
                >
                  {/* Custom Checkbox */}
                  <div className={`w-4 h-4 border rounded mr-2 flex-shrink-0 flex items-center justify-center ${selectedDiscountType === 'general' ? 'bg-primary-600 border-primary-600' : 'bg-white border-gray-300'
                    }`}>
                    {selectedDiscountType === 'general' && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                    )}
                  </div>
                  <span className="truncate">General Offers ({allOffers.filter(o => o.discountType === 'general').length})</span>
                </div>
              </div>
            </div>

          </div>


          <div className="hidden md:block w-full bg-white rounded-xl shadow-lg p-6 mb-6 md:mb-0 border border-primary-100">
            <nav className="flex flex-col gap-2 text-sm text-gray-700">
              {NavLinks.map(link => (
                <Link
                  key={link.name}
                  className="hover:text-primary-600 font-medium transition-colors"
                  href={link.href}
                >
                  See All {link.name}
                </Link>
              ))}
            </nav>
            <div className="mt-8 w-full flex justify-center">
              <div className="w-full">
                <Carousel orientation="vertical" />
              </div>
            </div>
          </div>
        </aside>

        <main className="flex-1 overflow-x-hidden">

          {/* Mobile Store Info Card  */}
          <div className="md:hidden w-full mb-4">
            <div className="bg-primary-200 rounded-xl shadow-lg px-4 py-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                <Image
                    src={store.icon ||
                      store.media_link ||
                      "/assets/OfferCodeFlight.jpg"}
                    alt={store.name}
                    width={56}
                    height={56}
                    className="object-contain w-14 h-14 rounded-xl border border-primary-300 shadow-sm"
                  />
                  <div>
                    <h1 className="text-xl font-bold text-gray-900">{store.brandHeading ? store.brandHeading : store.name}</h1>
                    {(store.categories && store.categories.length > 0) ? (
                      <div className="flex flex-wrap gap-1">
                        {store.categories.map((cat, idx) => (
                          <Link
                            key={idx}
                            href={`/category/${cat}`}
                            className="capitalize text-xs font-medium cursor-pointer hover:underline"
                          >
                            ({cat?.replaceAll("_", " ")})
                          </Link>
                        ))}
                      </div>
                    ) : store.category && (
                      <Link
                        href={`/category/${store.category}`}
                        className="capitalize text-primary-600 text-xs font-medium cursor-pointer hover:underline"
                      >
                        ({store.category?.replaceAll("_", " ")})
                      </Link>
                    )}
                    <div className="text-[10px] flex items-center gap-1 text-green-600 mt-1">
                      <CheckCircleIcon className="w-3 h-3" /> Verified: {lastVerified}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 text-center flex-shrink-0">
                  <div>
                    <p className="text-xl font-extrabold text-primary-600 leading-none">{Number(store.coupons)}</p>
                    <p className="text-xs text-gray-500 font-medium mt-1">Coupons</p>
                  </div>
                  <div className="w-px h-10 bg-gray-700"></div>
                  <div>
                    <p className="text-xl font-extrabold text-primary-600 leading-none">{Number(store.offersCount) - Number(store.coupons) || 0}</p>
                    <p className="text-xs text-gray-500 font-medium mt-1">Offers</p>
                  </div>
                </div>
              </div>

              <div className="mt-4 border-t border-gray-100 pt-3">
                <div className="text-gray-700 font-semibold mb-1 flex items-center gap-1">
                  <Info className="w-4 h-4 text-primary-500" /> Description
                </div>
                <p className="text-gray-600 text-xs">{store.description}</p>
              </div>
            </div>
          </div>

          {/* Desktop Store Header  */}
          <div className="text-white rounded-xl p-1 mb-8 hidden md:block">
            <div className="flex items-center gap-6">
            {/* <Image
                    src={store.icon ||
                      store.media_link ||
                      "/assets/OfferCodeFlight.jpg"}
                    alt={store.name}
                width={96}
                height={96}
                className="w-24 h-24 bg-white object-contain rounded-xl border shadow-md"
              /> */}
              <Image
                    src={store.icon ||
                      store.media_link ||
                      "/assets/OfferCodeFlight.jpg"}
                    alt={store.name}
                    width={56}
                    height={56}
                    className="object-contain bg-white w-24 h-24 rounded-xl border shadow-sm"
                  />
              <div className="flex-1">
                <div className="flex">
                  <h1 className="text-3xl font-bold mb-1">{store.brandHeading ? store.brandHeading : store.name}</h1>

                  {store.website && (
                    <a
                      href={`${store.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-sm font-semibold flex items-center ml-4 transition-colors"
                    >

                      <ExternalLinkIcon className="w-4 h-4" />
                    </a>
                  )}
                </div>

                {(store.categories && store.categories.length > 0) ? (
                <div className="flex flex-wrap gap-2 mb-1">
                  {store.categories.map((cat, idx) => (
                    <div
                      key={idx}
                      onClick={() => router.push(`/category/${cat}`)}
                      className="capitalize text-sm font-medium cursor-pointer hover:underline"
                    >
                      {cat?.replaceAll("_", " ")}
                    </div>
                  ))}
                </div>
              ) : store.category && (
                <div
                  onClick={() => router.push(`/category/${store.category}`)}
                  className="capitalize text-sm font-medium mb-1 cursor-pointer hover:underline"
                  >
                    {store.category?.replaceAll("_", " ")}
                </div>
              )}

                {/* <p className="text-sm font-medium mt-2">
                  <span className="font-bold">{store.coupons}</span> Coupons available
                </p> */}
              </div>
            </div>

            <div className="mt-3 border-t border-gray-100 pt-4">
              <div className="font-semibold mb-2">About {store.name}</div>
              <p className="text-sm">{store.description}</p>
              <div className="text-sm mt-3 flex items-center gap-2">
                Status:
                <span className={`font-semibold px-2 py-0.5 rounded-full text-xs ${store.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {store.isActive ? "Active" : "Inactive"}
                </span>
              </div>

              {/* {store.website && (
                <a
                  href={`${store.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white text-sm font-semibold lg:pt-2 flex items-center gap-1 transition-colors"
                >
                  Visit Store
                  <ExternalLinkIcon className="w-4 h-4" />
                </a>
              )} */}
            </div>
          </div>

          {/* Offer List Header - Reflects filtered count */}
          <h2 className="text-xl font-bold mb-4 text-gray-800 px-2 md:px-0 md:pt-8 pt-3">
            Available Offers and Coupons ({offers.length})
            {selectedCategorySlug && (
              <span className="text-sm font-normal text-primary-600 ml-2">
                (Filtered by Category: {categories.find(c => c.slug === selectedCategorySlug)?.name || selectedCategorySlug})
              </span>
            )}
            {selectedDiscountType && (
              <span className="text-sm font-normal text-primary-600 ml-2">
                (Filtered by Type: {selectedDiscountType === 'code' ? 'Coupon Codes' : 'General Offers'})
              </span>
            )}
          </h2>

          <div className="space-y-4">
            {offers.length === 0 ? (
              <div className="p-8 text-center bg-white rounded-xl shadow-lg text-gray-500">
                No offers or coupons currently available for **{store.name}** in the current filters.
              </div>
            ) : (
              offers.map((offer) => (
                <div
                  key={offer._id}
                  className="bg-white border border-gray-200 shadow-sm mb-3 md:p-2 lg:p-2 p-1"
                >
                  <div
                    className="p-4 pt-3 flex flex-col sm:flex-row sm:gap-4 items-start"
                    onClick={() => {
                      if (window.innerWidth < 640) {
                        if (offer.discountType === "code") {
                          handleCouponActionClick(offer);
                        } else {
                          handleGeneralActionClick(offer);
                        }
                      }
                    }}
                  >
                    <div className="w-full sm:w-8/12 space-y-1 cursor-pointer sm:cursor-default">
                      <h2 className="text-md lg:text-xl md:text-xl font-bold lg:line-clamp-1 text-gray-900">
                        {offer.title}
                      </h2>
                      <p className="text-xs text-gray-600 flex justify-between items-center">
                        {offer.description}
                        <span className="sm:hidden text-gray-500 ml-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                        </span>
                      </p>

                    </div>

                    <div className="w-full sm:w-4/12 flex flex-col items-end pt-3 sm:pt-0">
                      <div className="hidden sm:flex w-full">

                        {/* 1. Primary Button (GET DEAL - Blue) */}
                        {offer.discountType === "general" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGeneralActionClick(offer);
                            }}
                            className={`flex-1 cursor-pointer bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2.5 px-3 transition-colors text-sm rounded-lg`}
                          >
                            <span>GET DEAL</span>
                          </button>
                        )}

                        {/* 2. Primary Button (UNLOCK COUPON CODE - Orange) */}
                        {offer.discountType === "code" && offer.couponCode && (
                          <div className="w-full flex flex-col items-center">
                            <div className="w-full rounded-2xl p-4 relative">
                              <div className="text-center">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCouponActionClick(offer);
                                  }}
                                  className={`w-full cursor-pointer bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2`}
                                >
                                  <CopyIcon className="w-4 h-4" />
                                  <span>
                                    {copied === offer.couponCode
                                      ? "COPIED! GOING TO STORE..."
                                      : "UNLOCK COUPON CODE"}
                                  </span>
                                  <ExternalLinkIcon className="w-4 h-4" />
                                </button>

                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Mobile Footer */}
                  <div className="px-4 pb-3 flex justify-between items-center text-sm sm:hidden">
                    {offer.terms && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTerms(offer._id);
                        }}
                        className="flex items-center text-blue-600 hover:text-blue-800 transition-colors font-medium text-sm flex-shrink-0"
                      >
                        {openTermsId === offer._id ? 'Hide Details' : 'Show Details'}
                      </button>
                    )}

                    {/* Unified Info Display */}
                    <div className="flex items-center space-x-2 text-xs text-gray-600 flex-shrink">
                      <div className="flex items-center gap-1 text-green-600 font-medium">
                        <CheckCircleIcon className="w-3 h-3 fill-green-500 text-white" />
                        Verified
                      </div>
                      {/* <span className="text-gray-400">•</span>
                      <div className="text-gray-600">
                        <span>{offer.usedBy || '0'} uses today</span>
                      </div> */}
                    </div>

                    <div className="flex-shrink-0 w-auto">
                      {offer.discountType === "general" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGeneralActionClick(offer);
                          }}
                          className={`cursor-pointer bg-blue-500 hover:bg-blue-600 text-white font-semibold py-1.5 px-3 transition-colors text-xs rounded-lg`}
                        >
                          <span>GET DEAL</span>
                        </button>
                      )}

                      {offer.discountType === "code" && offer.couponCode && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCouponActionClick(offer);
                          }}
                          className={`cursor-pointer bg-orange-500 hover:bg-orange-600 text-white font-semibold py-1.5 px-3 rounded-lg transition-colors text-xs`}
                        >
                          <span>
                            {copied === offer.couponCode
                              ? "COPIED"
                              : "GET CODE"}
                          </span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Desktop Footer */}
                  <div className="border-t border-gray-200 p-3 bg-gray-50 hidden sm:flex justify-between items-center text-sm text-gray-600">
                    <div className="flex items-center space-x-4 text-xs">
                      <div className="flex items-center gap-1 text-green-600 font-medium">
                        <CheckCircleIcon className="w-3 h-3 fill-green-500 text-white" />
                        Verified
                      </div>
                      <div className="flex items-center gap-1 text-gray-500">
                        <UsersIcon className="w-3 h-3 text-gray-500" />
                        <span>{offer.usedBy || '0'} uses today</span>
                      </div>
                      {offer.terms && (
                        <button
                          onClick={() => toggleTerms(offer._id)}
                          className="flex items-center text-blue-600 hover:text-blue-800 transition-colors font-medium text-sm"
                        >
                          {openTermsId === offer._id ? 'Hide Details' : 'Show Details'}
                        </button>
                      )}
                    </div>
                    <div className="flex items-center">
                      {offer.allowRedirectUrl && (
                        <button
                          onClick={() => {
                            if (offer?.redirectUrl) {
                              window.open(offer.redirectUrl, "_blank", "noopener,noreferrer");
                            }
                          }}
                          className="flex items-center gap-1 text-gray-500 hover:text-blue-600 transition-colors font-medium text-sm"
                        >
                          <ExternalLinkIcon className="w-4 h-4" />
                          <span>Visit Website</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Terms & Conditions Accordion */}
                  {offer.terms && openTermsId === offer._id && (
                    <div className="p-4 pt-2 text-xs text-gray-700 bg-gray-50 border-t border-gray-200">
                      <h4 className="font-semibold mb-1">Terms & Conditions:</h4>
                      <p>{offer.terms}</p>
                    </div>
                  )}
                </div>
              ))
            )}
            {store.contentDescription && (
              <div className="bg-white rounded-xl shadow-xl lg:p-10 p-5 mt-8 border border-t-4 border-primary-500 overflow-hidden">
                <h2 className="text-2xl font-extrabold text-gray-900 mb-5 border-b pb-3 border-gray-100 flex items-center gap-2">
                  <Info className="w-6 h-6 text-primary-600" />
                  Detailed Information About {store.name}
                </h2>
                <div
                  className="text-gray-700 text-base leading-relaxed space-y-6"
                  dangerouslySetInnerHTML={createMarkup()}
                />
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}